import {faGear} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {getMenuItemNameForAnalytics} from '@rdcomponents/navigation/navigation-helper';
import {nameForAnalytics} from '@rdcomponents/navigation/navigation-type';

import {appRoute} from '../../../../app-route';
import {ChangeEmail} from '../../../../page/auth/change-email/change-email';
import {AccountsLinkingDefault} from '../../../../page/main/account-linking/accounts-linking-default';
import {CompaniesGroup} from '../../../../page/main/companies-group/companies-group';
import {CompaniesGroupActivation} from '../../../../page/main/companies-group/companies-group-activation';
import {CompaniesGroupCreate} from '../../../../page/main/companies-group-create/companies-group-create';
import {CompaniesGroupEdit} from '../../../../page/main/companies-group-edit/companies-group-edit';
import {IntegrationIntraService} from '../../../../page/main/integrations/integration-intra-service/integration-intra-service';
import {IntegrationUsedesk} from '../../../../page/main/integrations/integration-usedesk/integration-usedesk';
import {Integrations} from '../../../../page/main/integrations/integrations';
import {Notification} from '../../../../page/main/notification/notification';
import {Profile} from '../../../../page/main/profile/profile';
import {CreateUser} from '../../../../page/main/settings/users/form/create/create-user';
import {EditUserPageGuard} from '../../../../page/main/settings/users/form/edit/edit-user-page-guard';
import {Users} from '../../../../page/main/settings/users/users';
import {SourceAccountsPage} from '../../../../page/main/source-settings/pages/source-accounts-page/source-accounts-page';
import {SourceCatalogPage} from '../../../../page/main/source-settings/pages/source-catalog-page/source-catalog-page';
import {SourceSettingsIndex} from '../../../../page/main/source-settings/source-settings-index';
import {useDomainConfig} from '../../../../provider/domain-config/domain-config-hook';
import {useLicenses} from '../../../../provider/license/license-hook';
import {useLocale} from '../../../../provider/locale/locale-hook';
import {FeaturesEnum} from '../../../../service/user/user-type';
import {NavigationItemConfigType} from '../routing-type';

// eslint-disable-next-line complexity
export function useSettingsNavigation(): Array<NavigationItemConfigType> {
    const {isUserProfileShow, isSourceSettingsShown} = useDomainConfig();
    const {licenses, availableFeatures, enabledFeatures} = useLicenses();

    const {getLocalizedString} = useLocale();

    return [
        {
            isAvailable: availableFeatures[FeaturesEnum.settings] && enabledFeatures.includes(FeaturesEnum.settings),
            path: appRoute.settings.path,
            icon: <FontAwesomeIcon icon={faGear} />,
            label: getLocalizedString('CATEGORY_NAME__SETTINGS'),
            [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__SETTINGS'),
            exact: false,
            redirectPath: appRoute.settingsProfile.path,
            deniedPath: [appRoute.settings.path],
            children: [
                {
                    isAvailable: isUserProfileShow,
                    path: appRoute.settingsProfile.path,
                    label: getLocalizedString('CATEGORY_NAME__PROFILE'),
                    [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__REVIEWS_GENERATOR__QRCODE'),
                    exact: true,
                    component: Profile,
                },
                {
                    isOnlyRoute: true,
                    isAvailable: isUserProfileShow,
                    path: appRoute.changeEmail.path,
                    exact: true,
                    component: ChangeEmail,
                },
                {
                    isAvailable:
                        availableFeatures[FeaturesEnum.userManagement] &&
                        licenses[FeaturesEnum.userManagement]?.isActive &&
                        enabledFeatures.includes(FeaturesEnum.userManagement),
                    path: appRoute.users.path,
                    label: getLocalizedString('CATEGORY_NAME__USERS'),
                    [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__USERS'),
                    exact: true,
                    component: Users,
                },
                {
                    isOnlyRoute: true,
                    isAvailable:
                        availableFeatures[FeaturesEnum.userManagement] &&
                        licenses[FeaturesEnum.userManagement]?.isActive &&
                        enabledFeatures.includes(FeaturesEnum.userManagement),
                    path: appRoute.createUser.path,
                    exact: true,
                    component: CreateUser,
                },
                {
                    isOnlyRoute: true,
                    isAvailable:
                        availableFeatures[FeaturesEnum.userManagement] &&
                        licenses[FeaturesEnum.userManagement]?.isActive &&
                        enabledFeatures.includes(FeaturesEnum.userManagement),
                    path: appRoute.editUser.path,
                    exact: true,
                    component: EditUserPageGuard,
                },
                {
                    path: appRoute.accountLinkingSettings.path,
                    label: getLocalizedString('CATEGORY_NAME__ACCOUNTS_LINKING'),
                    [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__ACCOUNTS_LINKING'),
                    exact: true,
                    component: AccountsLinkingDefault,
                },
                {
                    isAvailable:
                        availableFeatures[FeaturesEnum.companyGroups] &&
                        enabledFeatures.includes(FeaturesEnum.companyGroups),
                    isActivated: licenses[FeaturesEnum.companyGroups]?.isActive,
                    path: appRoute.settingsCompaniesGroup.path,
                    label: getLocalizedString('CATEGORY_NAME__GROUPS_OF_COMPANIES'),
                    [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__GROUPS_OF_COMPANIES'),
                    exact: true,
                    component: licenses[FeaturesEnum.companyGroups]?.isActive
                        ? CompaniesGroup
                        : CompaniesGroupActivation,
                },
                {
                    isAvailable: isUserProfileShow,
                    path: appRoute.settingsNotification.path,
                    label: getLocalizedString('CATEGORY_NAME__NOTIFICATIONS'),
                    [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__NOTIFICATIONS'),
                    exact: true,
                    component: Notification,
                },
                {
                    isOnlyRoute: true,
                    isAvailable:
                        availableFeatures[FeaturesEnum.companyGroups] &&
                        licenses[FeaturesEnum.companyGroups]?.isActive &&
                        enabledFeatures.includes(FeaturesEnum.companyGroups),
                    path: appRoute.settingsCompaniesGroupCreate.path,
                    exact: true,
                    component: licenses[FeaturesEnum.companyGroups]?.isActive
                        ? CompaniesGroupCreate
                        : CompaniesGroupActivation,
                },
                {
                    isOnlyRoute: true,
                    isAvailable:
                        availableFeatures[FeaturesEnum.companyGroups] &&
                        licenses[FeaturesEnum.companyGroups]?.isActive &&
                        enabledFeatures.includes(FeaturesEnum.companyGroups),
                    path: appRoute.settingsCompaniesGroupEdit.path,
                    exact: true,
                    component: licenses[FeaturesEnum.companyGroups]?.isActive
                        ? CompaniesGroupEdit
                        : CompaniesGroupActivation,
                },
                {
                    isAvailable:
                        availableFeatures[FeaturesEnum.usedeskIntegration] &&
                        enabledFeatures.includes(FeaturesEnum.usedeskIntegration),
                    path: appRoute.settingsIntegrations.path,
                    label: getLocalizedString('CATEGORY_NAME__INTEGRATION'),
                    [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__INTEGRATION'),
                    exact: true,
                    component: Integrations,
                },
                {
                    isAvailable: isSourceSettingsShown,
                    path: appRoute.settingsSourceSettings.path,
                    label: getLocalizedString('CATEGORY_NAME__SOURCE_SETTINGS'),
                    [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__SOURCE_SETTINGS'),
                    exact: true,
                    component: SourceSettingsIndex,
                },
                {
                    isOnlyRoute: true,
                    path: appRoute.settingsIntegrationIntraService.path,
                    exact: true,
                    component: IntegrationIntraService,
                },
                {
                    isAvailable:
                        availableFeatures[FeaturesEnum.usedeskIntegration] &&
                        enabledFeatures.includes(FeaturesEnum.usedeskIntegration),
                    isOnlyRoute: true,
                    path: appRoute.settingsIntegrationUsedesk.path,
                    exact: true,
                    component: IntegrationUsedesk,
                    deniedPath: [appRoute.settingsIntegrationUsedesk.oldPath],
                },
                {
                    isOnlyRoute: true,
                    isAvailable: isSourceSettingsShown,
                    path: appRoute.settingsSourceSettingsCatalog.path,
                    label: getLocalizedString('CATEGORY_NAME__SOURCE_SETTINGS'),
                    [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__SOURCE_SETTINGS'),
                    exact: true,
                    component: SourceCatalogPage,
                },
                {
                    isOnlyRoute: true,
                    isAvailable: isSourceSettingsShown,
                    path: appRoute.settingsSourceSettingsAccount.path,
                    label: getLocalizedString('CATEGORY_NAME__SOURCE_SETTINGS'),
                    [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__SOURCE_SETTINGS'),
                    exact: true,
                    component: SourceAccountsPage,
                },
            ],
        },
    ];
}
